import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Checkbox } from './ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONSENT_LABEL = 'Send me the latest news and insights from YourNest Venture Capital';

const errorMessage = (err) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (err.response?.status === 422) return 'Please enter a valid email address.';
  return 'Something went wrong. Please try again in a moment.';
};

/** Newsletter signup form for the dark footer. Signups appear in Admin > Subscribers. */
export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot - real visitors never see or fill this
  const [status, setStatus] = useState('idle'); // idle | submitting | done
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    if (!consent) {
      setError('Please tick the box to confirm you want to receive our newsletter.');
      return;
    }
    setError('');
    setStatus('submitting');
    try {
      await axios.post(`${API}/newsletter-signup`, { email: email.trim(), consent, website });
      setStatus('done');
    } catch (err) {
      setError(errorMessage(err));
      setStatus('idle');
    }
  };

  return (
    <div className="text-white max-w-md" data-testid="newsletter-signup">
      {status === 'done' ? (
        <div className="flex items-start gap-3" role="status">
          <CheckCircle2 className="w-6 h-6 text-[#E07B6E] shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h4 className="font-semibold text-white">Thank you for subscribing</h4>
            <p className="text-gray-300 mt-1">You&apos;ll receive our latest newsletter in your inbox.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <h4 className="font-semibold text-white mb-4">Subscribe to our newsletter</h4>

          <label htmlFor="newsletter-email" className="block mb-2">
            Work email address
          </label>
          <input
            id="newsletter-email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={error ? 'newsletter-error' : undefined}
            aria-invalid={error ? true : undefined}
            className="w-full h-12 rounded-lg bg-white text-[#211F1F] px-4 text-base outline-none focus-visible:ring-2 focus-visible:ring-[#E07B6E]"
          />

          <div className="flex items-start gap-3 mt-5">
            <Checkbox
              id="newsletter-consent"
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              className="mt-1 h-5 w-5 bg-white border-white text-white data-[state=checked]:bg-[#C8473A] data-[state=checked]:border-[#C8473A] data-[state=checked]:text-white focus-visible:ring-2 focus-visible:ring-[#E07B6E]"
            />
            <label htmlFor="newsletter-consent" className="text-sm leading-snug cursor-pointer">
              {CONSENT_LABEL}
            </label>
          </div>

          {/* Honeypot: hidden from people and assistive tech, bots tend to fill it in. */}
          <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="newsletter-website">Website</label>
            <input
              id="newsletter-website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          {error && (
            <p id="newsletter-error" role="alert" className="mt-4 text-sm text-[#FFB4AB]">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#C8473A] hover:bg-[#B74F40] disabled:opacity-70 disabled:cursor-not-allowed text-white font-medium px-8 h-12 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#211F1F]"
          >
            {status === 'submitting' && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            Subscribe
          </button>

          <p className="mt-5 text-xs text-gray-400">
            We&apos;ll only use your email to send our newsletter. See our{' '}
            <Link to="/privacy-policy" className="underline hover:text-white">
              Privacy Policy
            </Link>
            .
          </p>
        </form>
      )}
    </div>
  );
}
